import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, Clock, CheckCircle2, XCircle, Upload } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWalletRequests() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveDialog, setApproveDialog] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchRequests(); }, [token]);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/crypto/requests`, { headers: { Authorization: `Bearer ${token}` } });
      // Enrich with client names
      const enriched = await Promise.all((res.data || []).map(async (r) => {
        try {
          const clientRes = await axios.get(`${API}/clients/${r.client_id}`, { headers: { Authorization: `Bearer ${token}` } });
          const u = clientRes.data?.user || clientRes.data;
          return { ...r, client_name: `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || r.client_id };
        } catch { return { ...r, client_name: r.client_id }; }
      }));
      setRequests(enriched);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleApprove = async () => {
    if (!walletAddress) { toast.error('Adresse wallet requise'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/crypto/requests/${selectedReq.id}`, {
        action: 'approve', wallet_address: walletAddress, qr_code_url: qrCode,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Wallet assigné !');
      setApproveDialog(false);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleReject = async (reqId) => {
    if (!window.confirm('Refuser cette demande ?')) return;
    try {
      await axios.put(`${API}/crypto/requests/${reqId}`, { action: 'reject' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demande refusée');
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const pending = requests.filter(r => r.status === 'pending');
  const processed = requests.filter(r => r.status !== 'pending');

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="spinner" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-wallets-page">
        <h1 className="text-2xl font-bold">Demandes de Wallets</h1>

        {/* Pending */}
        <Card className="border-yellow-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-400">
              <Clock className="w-5 h-5" /> En attente ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pending.length === 0 ? (
              <p className="text-center text-muted-foreground py-6">Aucune demande en attente</p>
            ) : (
              <div className="space-y-3">
                {pending.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-4 rounded-lg border border-yellow-500/20 bg-yellow-500/5" data-testid={`wallet-req-${r.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center font-bold text-yellow-400">{r.currency_code?.[0] || '?'}</div>
                      <div>
                        <p className="font-semibold">{r.currency_name || r.currency_code}</p>
                        <p className="text-sm text-muted-foreground">Réseau: {r.network}</p>
                        <p className="text-xs text-muted-foreground">
                          Client: <button onClick={() => navigate(`/admin/clients/${r.client_id}?tab=wallets`)} className="underline hover:text-primary">{r.client_name}</button>
                          {' · '}{new Date(r.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => { setSelectedReq(r); setWalletAddress(''); setQrCode(''); setApproveDialog(true); }} data-testid={`approve-${r.id}`}>
                        Traiter
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleReject(r.id)} data-testid={`reject-${r.id}`}>
                        Refuser
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Processed */}
        {processed.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Historique</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {processed.slice(0, 20).map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20">
                    <div>
                      <span className="font-semibold">{r.currency_name || r.currency_code}</span>
                      <span className="text-sm text-muted-foreground ml-3">{r.client_name}</span>
                    </div>
                    <Badge className={r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                      {r.status === 'approved' ? <><CheckCircle2 className="w-3 h-3 mr-1" />Approuvé</> : <><XCircle className="w-3 h-3 mr-1" />Refusé</>}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Approve Dialog */}
        <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
          <DialogContent className="max-w-md" data-testid="approve-wallet-dialog">
            <DialogHeader><DialogTitle>Assigner le wallet {selectedReq?.currency_name}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-secondary/30 rounded-lg text-sm">
                <p><span className="text-muted-foreground">Client:</span> {selectedReq?.client_name}</p>
                <p><span className="text-muted-foreground">Crypto:</span> {selectedReq?.currency_name} ({selectedReq?.currency_code})</p>
                <p><span className="text-muted-foreground">Réseau:</span> {selectedReq?.network}</p>
              </div>
              <div>
                <Label>Adresse du wallet *</Label>
                <Input value={walletAddress} onChange={e => setWalletAddress(e.target.value)} placeholder="0x..." className="font-mono" data-testid="approve-wallet-address" />
              </div>
              <div>
                <Label>QR Code</Label>
                <div className="space-y-2">
                  <Input value={qrCode} onChange={e => setQrCode(e.target.value)} placeholder="URL ou coller le QR code..." />
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm transition-colors">
                    <Upload className="w-4 h-4" /> Importer image QR
                    <input type="file" accept="image/*" className="hidden" onChange={e => {
                      const file = e.target.files?.[0]; if (!file) return; e.target.value = '';
                      const reader = new FileReader();
                      reader.onload = ev => { setQrCode(ev.target.result); toast.success('QR importé'); };
                      reader.readAsDataURL(file);
                    }} />
                  </label>
                  {qrCode && qrCode.startsWith('data:') && <img src={qrCode} alt="QR" className="w-20 h-20 bg-white rounded p-1" />}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproveDialog(false)}>Annuler</Button>
              <Button onClick={handleApprove} disabled={saving} data-testid="confirm-approve-wallet">{saving ? 'Assignation...' : 'Approuver et assigner'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
